import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Alert, TouchableOpacity, Text } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  getChatMessages,
  sendChatMessage,
  clearChatMessages,
} from '@cubemeet/core/store/slices/chatSlice';
import { useRealtimeChat } from '@cubemeet/core';
import {
  formatMessageTime,
  formatMessageDate,
  getMessageSenderName,
  mergeMessages,
  type ChatMessage,
} from '@cubemeet/core/modules/chat';
import {
  ChatMessageList,
  ChatInputBar,
} from '@cubemeet/ui';
import ModuleHeader from '../../components/common/ModuleHeader';
import { useNavigate } from 'react-router-dom';
import { useChatI18n } from './useChatI18n';

interface TravelChatProps {
  groupId: string;
  groupName: string;
  onBack?: () => void;
}

/**
 * Web 端旅行聊天组件
 * 
 * 使用共享的原子化组件和 Realtime 功能
 */
export const TravelChat: React.FC<TravelChatProps> = ({
  groupId,
  groupName,
  onBack,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const scrollViewRef = useRef<ScrollView>(null);
  const { t, language } = useChatI18n();

  const { user } = useSelector((state: RootState) => state.auth);
  const { messages: storeMessages, loading, error } = useSelector(
    (state: RootState) => state.chat
  );

  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>(storeMessages);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const EMOJI_LIST = ['😀', '😂', '😍', '👍', '🔥', '❤️', '🎉', '🤔', '😭', '👏', '🙏', '👀', '🚀', '💯'];

  // 初始加载消息
  useEffect(() => {
    dispatch(getChatMessages(groupId));

    return () => {
      dispatch(clearChatMessages());
    };
  }, [dispatch, groupId]);

  // 同步 Redux store 到本地状态
  useEffect(() => {
    setLocalMessages(storeMessages);
  }, [storeMessages]);

  // 处理 Realtime 新消息
  const handleMessageReceived = useCallback((message: ChatMessage) => {
    console.log('📨 Realtime new message:', message);
    setLocalMessages((prev) => mergeMessages(prev, [message]));
    scrollToBottom();
  }, []);

  // 处理消息更新
  const handleMessageUpdated = useCallback((message: ChatMessage) => {
    console.log('📝 Realtime message updated:', message);
    setLocalMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = message;
        return updated;
      }
      return prev;
    });
  }, []);

  // 处理消息删除
  const handleMessageDeleted = useCallback((messageId: string) => {
    console.log('🗑️ Realtime message deleted:', messageId);
    setLocalMessages((prev) => prev.filter((m) => m.id !== messageId));
  }, []);

  // 启用 Realtime 订阅
  const { isConnected, error: realtimeError } = useRealtimeChat({
    groupId,
    onMessageReceived: handleMessageReceived,
    onMessageUpdated: handleMessageUpdated,
    onMessageDeleted: handleMessageDeleted,
    enabled: true,
  });

  // 显示 Realtime 连接状态
  useEffect(() => {
    if (realtimeError) {
      console.error('❌ Realtime error:', realtimeError);
    }
    if (isConnected) {
      console.log('✅ Realtime connected for group:', groupId);
    }
  }, [isConnected, realtimeError, groupId]);

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, []);

  // 发送消息
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    setSending(true);
    const messageText = inputMessage.trim();
    setInputMessage(''); // 立即清空输入框

    try {
      await dispatch(
        sendChatMessage({
          groupId,
          message: messageText,
          messageType: 'text',
        })
      ).unwrap();

      scrollToBottom();
    } catch (error: any) {
      // 发送失败，恢复输入内容
      setInputMessage(messageText);
      Alert.alert(
        t('travelChat.errors.sendFailed'),
        error || t('travelChat.errors.networkError')
      );
    } finally {
      setSending(false);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setInputMessage((prev) => (prev || '') + emoji);
    setShowEmojiPicker(false);
    // focus the input if possible (best-effort)
    try { /* nothing to call here; ChatInputBar holds inputRef */ } catch (e) {}
  };


  // 格式化时间
  const formatTime = useCallback(
    (dateString: string) => {
      return formatMessageTime(
        dateString,
        language === 'zh-CN' ? 'zh-CN' : 'en-US'
      );
    },
    [language]
  );

  // 格式化日期
  const formatDate = useCallback(
    (dateString: string) => {
      return formatMessageDate(
        dateString,
        language === 'zh-CN' ? 'zh-CN' : 'en-US',
        {
          today: t('travelChat.time.today'),
          yesterday: t('travelChat.time.yesterday'),
        }
      );
    },
    [language, t]
  );

  // 获取发送者名称
  const getSenderName = useCallback(
    (message: ChatMessage) => {
      return getMessageSenderName(message, t('travelChat.user.unknown'));
    },
    [t]
  );

  return (
    <View style={styles.container}>
      {/* 使用共享的 ModuleHeader 组件 */}
      <ModuleHeader
        moduleType="travel"
        title={`${groupName} Chat`}
        subtitle={
          isConnected
            ? t('travelChat.header.subtitle')
            : t('travelChat.status.disconnected')
        }
        // Use close button for deterministic exit; hide left back arrow
        showBackButton={false}
        onBack={() => {
          if (typeof onBack === 'function') {
            try { onBack(); return; } catch (e) {}
          }
          if (typeof window !== 'undefined' && window.history && typeof window.history.back === 'function') {
            window.history.back();
            return;
          }
          if (typeof window !== 'undefined') window.location.hash = '#travel';
        }}
        showCloseButton={true}
        onClose={() => {
          // Close icon should deterministically return user to the TripDetail
          // standalone page which reliably loads the travel detail for a
          // given groupId.
          try {
            navigate(`/travel/${groupId}`);
            return;
          } catch (e) {
            if (typeof window !== 'undefined') {
              // fallback to full redirect if SPA navigation isn't available
              try { window.location.href = `/travel/${groupId}`; } catch (_) { window.location.hash = `#travel?groupId=${groupId}`; }
            }
          }
        }}
        showMiniCube={true}
      />

      {/* 聊天区域 */}
      <View style={styles.chatContainer}>
        {/* 消息列表 */}
        <ChatMessageList
          messages={localMessages}
          currentUserId={user?.id}
          themeColor="#FF6B6B"
          loading={loading}
          formatTime={formatTime}
          formatDate={formatDate}
          getSenderName={getSenderName}
          loadingText={t('travelChat.loading.text')}
          emptyState={{
            icon: 'chat-bubble-outline',
            title: t('travelChat.empty.title'),
            subtitle: t('travelChat.empty.subtitle'),
          }}
          scrollRef={scrollViewRef}
          onContentSizeChange={scrollToBottom}
        />

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <View style={styles.emojiPickerContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
              {EMOJI_LIST.map(e => (
                <TouchableOpacity key={e} onPress={() => handleEmojiSelect(e)} style={styles.emojiItem}>
                  <Text style={styles.emojiText}>{e}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* 输入框 */}
        <ChatInputBar
          value={inputMessage}
          onChangeText={setInputMessage}
          onSend={handleSendMessage}
          onEmojiPress={() => { setShowEmojiPicker(!showEmojiPicker); }}
          placeholder={t('travelChat.input.placeholder')}
          sending={sending}
          disabled={!isConnected}
          maxLength={500}
          themeColor="#FF6B6B"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
  },
  chatContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
  },
  emojiPickerContainer: {
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingVertical: 8,
  },
  emojiItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emojiText: {
    fontSize: 28,
    lineHeight: 34,
  },
});
